import { MapPointInterface } from "../../../core/models/mapping";


export const intToOrdinal = (n:number) => {
	const ones = +n % 10, tens = +n % 100 - ones;
	return n + ["th","st","nd","rd"][ tens === 10 || ones > 3 ? 0 : ones ];
}

export const formatFloor = (strFloor: string) => {
	if (strFloor) {
		if ( /^\s*\d+\s*$/.test(strFloor) ) {
			strFloor = intToOrdinal(parseInt(strFloor)) + ' Floor'
		}
	}
	return strFloor
}

export interface KmlMapPointInterface extends MapPointInterface {
	//altitudeEllipsoidMeters: number;
}


export abstract class KmlBuilderPath {
	id: string;
	pathNumber: number;
	buildingId?: string;
	floor: string;
	points: KmlMapPointInterface[] = [];
};

function escapeXml(unsafe) {
    return unsafe.replace(/[<>&'"]/g, function (c) {
        switch (c) {
            case '<': return '&lt;';
            case '>': return '&gt;';
            case '&': return '&amp;';
            case '\'': return '&apos;';
            case '"': return '&quot;';
        }
    });
}

export class KmlBuilder {

	ptLabelScale = '0.7'
	ptIconScale = '1.75'
	viaPtIconScale = '0.6'
	phonePtIconScale = '0.5'
	disabledPtIconScale = '0.4'
	alphaColor = 'FF'
	state = {
		existingStyleIds: new Array<string>()
	};

	constructor(paths?: KmlBuilderPath[]) {
		this.paths = paths || [];
	}
	public paths : KmlBuilderPath[];


	protected kmlTop = (topDocName:string) => `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2" xmlns:gx="http://www.google.com/kml/ext/2.2" xmlns:kml="http://www.opengis.net/kml/2.2" xmlns:atom="http://www.w3.org/2005/Atom">
<Document>
	<name>${topDocName}</name>
	<open>1</open>
`;

	kmlBottom = () => `    </Document>
</kml>`

	protected kmlPtStyles = ( pt: KmlMapPointInterface, pathId: string ) => {
		const styleId = this.ptStyleId(pt,pathId);
		if ( this.state.existingStyleIds.indexOf(styleId) != -1 ) {
			// console.log(`kmlPtStyles exists: `, {styleId,pt});
			return '';  // don't add this style if it already exists
		}
		const bVia = pt.type == 'VIA'
		const bCallLocation = pt.type.toLowerCase().endsWith(' call');
		const bWhite = !pt || pt.type == 'HA' && pt.parentId == null
		// console.log(`kmlPtStyles pt.type: ${pt.type}`, {bVia,bCallLocation,bWhite,styleId,pt});
		let iconScale = this.ptIconScale;
		if (bVia) {
			iconScale = this.viaPtIconScale;
		} else if ( bCallLocation ) {
			iconScale = this.phonePtIconScale;
		}
		// console.log(`kmlPtStyles ${pt.pointId||pt.exPointId}} ${pt.type} ${pt.disabled}`, {bVia,bCallLocation,bWhite,styleId,pt});
		if ( pt.disabled ) {
			iconScale = this.disabledPtIconScale;
		}
		this.state.existingStyleIds.push(styleId)

		return `    <Style id="${styleId}">
		<IconStyle>
			<color>${bWhite?'FFFFFFFF':this.kmlColor(pt)}</color>
			<scale>${iconScale}</scale>
			<Icon>
				<href>http://maps.google.com/mapfiles/kml/shapes/${bCallLocation?'phone':'placemark_circle'}.png</href>
			</Icon>
		</IconStyle>
		<LabelStyle>
			<color>${this.kmlColor(pt)}</color>
			<scale>${this.ptLabelScale}</scale>
		</LabelStyle>
	</Style>
	<Style id="${this.lineStyleId(pt)}">
		<LineStyle>
			<color>${this.kmlColor(pt,bCallLocation?'11':undefined)}</color>
			<width>${bCallLocation?1:4}</width>
		</LineStyle>
	</Style>
`
	}

	protected ptStyleId = ( pt: KmlMapPointInterface, pathId: string ) =>
		`POINT-${pathId}_${pt.type?.replace(/\s*/g,'')}_${this.htmlColor(pt)}placemark${this.ptLabelScale}${this.ptIconScale}`

	protected lineStyleId = ( pt: KmlMapPointInterface ) =>
		`Line-${pt.type?.replace(/\s*/g,'')}_${this.htmlColor(pt)}`

	protected ptCoordinates = ( pt: KmlMapPointInterface, verticalOffset?: number ) =>
		`${pt.longitude},${pt.latitude},${verticalOffset==null?0:pt.altitudeEllipsoidMeters-verticalOffset}`

	protected htmlColor = ( pt: KmlMapPointInterface ) => pt ? `${pt.color?.replace(/^#/,'')}${this.alphaColor}` : 'FFFFFFFF'

	// KML color is backwards... HTML:RRGGBBAA ==> KML:AABBGGRR
	protected kmlColor = ( pt: MapPointInterface, alphaColor?: string ) => {
		const color = pt.color || '000000'
		return pt ? `${alphaColor??this.alphaColor}${color.substring(5,7)}${color.substring(3,5)}${color.substring(1,3)}` : 'FFFFFFFF';
	}

	protected kmlPtFolder = ( pt: KmlMapPointInterface, prevPt: KmlMapPointInterface|undefined, pathId: string, folderIndex: number, verticalOffset?: number ) => {
		const bVia = pt.type == 'VIA'
		const ptId =  bVia ? 'VIA' : (pt.pointId || pt.exPointId)
		let name = `(${pt.name})`
		if ( ptId && !pt.disabled) {
			name += ' ' + ptId
		}
		const altitudeMode = 'absolute'; //verticalOffset==null? 'clampToGround' : 'absolute';
		const ptPlacemark =`<Placemark>
				<name>${escapeXml(name)}</name>
				<styleUrl>#${this.ptStyleId(pt,pathId)}</styleUrl>
				<Point>
					<altitudeMode>${altitudeMode}</altitudeMode>
					<coordinates>${this.ptCoordinates(pt,verticalOffset)}</coordinates>
				</Point>
			</Placemark>`

		const linePlacemark = prevPt ? `<Placemark>
				<name>${escapeXml(name)}</name>
				<styleUrl>#${this.lineStyleId(pt)}</styleUrl>
				<LineString>
					<tessellate>1</tessellate>
					<altitudeMode>${altitudeMode}</altitudeMode>
					<coordinates>
						${this.ptCoordinates(prevPt,verticalOffset)} ${this.ptCoordinates(pt,verticalOffset)}
					</coordinates>
				</LineString>
			</Placemark>` : ''

		return `        <Folder id="point_folder${folderIndex}">
		    <name>${escapeXml(name)}</name>
		    ${ptPlacemark}
		    ${linePlacemark}
	    </Folder>
`
	}

	public buildKml = ( params : {
		topDocName: string|null,
		mainFolderName: string,
		verticalOffset?: number,
		exportSubtitle?: string,
	}) => {
		console.log(`buildKml`, {params});
		this.state = {
			existingStyleIds: []
		}
		const buildingIds = this.paths.map(p => p.buildingId).filter((id,i,a) =>a.indexOf(id)==i).sort();
		let strKml = this.kmlTop(params.topDocName||`${buildingIds.join(' ')} - ${params.exportSubtitle??'Circet Export'}`)
		//const existingPtStylesByColor : {[color:string]:boolean} = {}
		for ( const path of this.paths ) {
			for( const pt of path.points ) {
				if ( pt ) {
					const ptColor = pt.color || '000000';
					// if ( existingPtStylesByColor[ptColor] ) {

					// } else {
						strKml += this.kmlPtStyles(pt,path.id)
					// 	existingPtStylesByColor[ptColor] = true
					// }
				}
			}
		}
		let totalPtCnt = 0
		let strPathsKml = ''
		for ( let pathIndex = 0; pathIndex < this.paths.length; pathIndex++ ) {
			const path = this.paths[pathIndex];
			const points = path.points;
			const strPathNumber = 'P' + `${path.pathNumber}`.padStart(2,'0')
			const strFloor = formatFloor(path.floor)
			strPathsKml += `    <Document>
        <name>${path.buildingId}${strPathNumber}${strFloor?' - '+strFloor:''} - GE View</name>
`
			points.forEach( (pt,index) => {
				totalPtCnt++
				const prevPt = points.find(aPt=>aPt.id == pt.parentId);
				strPathsKml += this.kmlPtFolder(pt,prevPt,path.id,totalPtCnt,params.verticalOffset)
			})
			strPathsKml += `    </Document>
`
		}

		if ( params.mainFolderName ) {
			strKml += `<Folder>
	<name>${params.mainFolderName}</name>
	<open>1</open>
	${strPathsKml}
</Folder>`
		} else {
			strKml += strPathsKml
		}

		strKml += this.kmlBottom()
		//console.dir(strKml)
		return strKml
	}
}

